import { createTheme, ThemeOptions, PaletteMode } from "@mui/material/styles";

// Extend the MUI Palette interface to include custom properties
declare module '@mui/material/styles' {
  interface Palette {
    tertiary: Palette['primary'];
    system: SystemPalette;
    greyscale: GreyscalePalette;
    button: ButtonPalette;
    input_field: InputFieldPalette;
  }

  interface PaletteOptions {
    tertiary?: PaletteOptions['primary'];
    system?: SystemPaletteOptions;
    greyscale?: GreyscalePaletteOptions;
    button?: ButtonPaletteOptions;
    input_field?: InputFieldPaletteOptions;
  }

  interface TypeBackground {
    bgShade_1?: string;
    bgShade_2?: string;
    bgShade_3?: string;
  }

  interface TypeText {
    tertiary?: string;
  }

  interface TypographyVariants {
    subtitle_m: React.CSSProperties;
    subtitle_s: React.CSSProperties;
    subtitle_xs: React.CSSProperties;
    body_l: React.CSSProperties;
    body_m: React.CSSProperties;
    body_s: React.CSSProperties;
    body_xs: React.CSSProperties;
    body_xxs: React.CSSProperties;
    button_l: React.CSSProperties;
    button_m: React.CSSProperties;
    button_s: React.CSSProperties;
    button_xs: React.CSSProperties;
    menu_tab: React.CSSProperties;
  }

  interface TypographyVariantsOptions {
    subtitle_m?: React.CSSProperties;
    subtitle_s?: React.CSSProperties;
    subtitle_xs?: React.CSSProperties;
    body_l?: React.CSSProperties;
    body_m?: React.CSSProperties;
    body_s?: React.CSSProperties;
    body_xs?: React.CSSProperties;
    body_xxs?: React.CSSProperties;
    button_l?: React.CSSProperties;
    button_m?: React.CSSProperties;
    button_s?: React.CSSProperties;
    button_xs?: React.CSSProperties;
    menu_tab?: React.CSSProperties;
  }
}

// Extend Typography component to accept custom variants
declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    subtitle_m: true;
    subtitle_s: true;
    subtitle_xs: true;
    body_l: true;
    body_m: true;
    body_s: true;
    body_xs: true;
    body_xxs: true;
    button_l: true;
    button_m: true;
    button_s: true;
    button_xs: true;
    menu_tab: true;
  }
}

// Define custom palette interfaces
interface SystemPalette {
  success: string;
  successHover: string;
  successSecondary: string;
  successTertiary: string;
  successClick: string;
  error: string;
  errorHover: string;
  errorSecondary: string;
  errorTertiary: string;
  errorClick: string;
  returned: string;
  returnedHover: string;
  returnedSecondary: string;
  returnedTertiary: string;
  returnedClick: string;
  pending: string;
  pendingHover: string;
  pendingSecondary: string;
  pendingTertiary: string;
  pendingClick: string;
  submit: string;
  submitHover: string;
  submitSecondary: string;
  submitTertiary: string;
  submitClick: string;
}

interface SystemPaletteOptions extends Partial<SystemPalette> {}

interface GreyscalePalette {
  icons: string;
  stroke_1: string;
  divider_1: string;
  disabledText: string;
  disabledBg: string;
  white: string;
}

interface GreyscalePaletteOptions extends Partial<GreyscalePalette> {}

interface ButtonPalette {
  // Primary Button
  primaryBg: string;
  primaryText: string;
  primaryBgHover: string;
  primaryTextHover: string;
  primaryBgFocused: string;
  primaryStrokeFocused: string;
  primaryTextFocused: string;
  primaryBgActive: string;
  primaryTextActive: string;
  primaryBgClick: string;
  primaryTextClick: string;
  primaryBgDisabled: string;
  primaryTextDisabled: string;

  // Secondary Button
  secondaryBg: string;
  secondaryText: string;
  secondaryBgHover: string;
  secondaryTextHover: string;
  secondaryBgFocused: string;
  secondaryStrokeFocused: string;
  secondaryTextFocused: string;
  secondaryBgActive: string;
  secondaryTextActive: string;
  secondaryBgClick: string;
  secondaryTextClick: string;
  secondaryBgDisabled: string;
  secondaryTextDisabled: string;

  // Cancel Button
  cancelBg: string;
  cancelText: string;
  cancelBgHover: string;
  cancelTextHover: string;
  cancelBgFocused: string;
  cancelStrokeFocused: string;
  cancelTextFocused: string;
  cancelBgActive: string;
  cancelTextActive: string;
  cancelBgClick: string;
  cancelTextClick: string;
  cancelBgDisabled: string;
  cancelTextDisabled: string;
}

interface ButtonPaletteOptions extends Partial<ButtonPalette> {}

interface InputFieldPalette {
  fill: string;
  stroke: string;
  text: string;
}

interface InputFieldPaletteOptions extends Partial<InputFieldPalette> {}

// Define breakpoints values
interface BreakpointValues {
  xs: number;
  sm: number;
  md: number;
  lg: number;
  xl: number;
}

// Base theme configuration
const baseTheme: ThemeOptions = {
  typography: {
    fontFamily: "Roboto, Arial, sans-serif",
    h1: {
      fontSize: "3.375rem",
      fontWeight: 700,
      lineHeight: "auto",
    },
    h2: {
      fontSize: "2.625rem",
      fontWeight: 700,
      lineHeight: "auto",
    },
    h3: {
      fontSize: "2rem",
      fontWeight: 700,
      lineHeight: "auto",
    },
    h4: {
      fontSize: "1.5rem",
      fontWeight: 700,
      lineHeight: "auto",
    },
    h5: {
      fontSize: "1.25rem",
      fontWeight: 700,
      lineHeight: "auto",
    },
    h6: {
      fontSize: "1.125rem",
      fontWeight: 700,
      lineHeight: "32px",
    },
    subtitle_m: {
      fontSize: "1rem",
      fontWeight: 500,
      lineHeight: "auto",
    },
    subtitle_s: {
      fontSize: "0.875rem",
      fontWeight: 500,
      lineHeight: "auto",
    },
    subtitle_xs: {
      fontSize: "0.75rem",
      fontWeight: 500,
      lineHeight: "auto",
    },
    body_l: {
      fontSize: "1.125rem",
      fontWeight: 400,
      lineHeight: "auto",
    },
    body_m: {
      fontSize: "1rem",
      fontWeight: 400,
      lineHeight: "32px",
    },
    body_s: {
      fontSize: "0.875rem",
      fontWeight: 400,
      lineHeight: "auto",
    },
    body_xs: {
      fontSize: "0.75rem",
      fontWeight: 400,
      lineHeight: "auto",
    },
    body_xxs: {
      fontSize: "0.625rem",
      fontWeight: 400,
      lineHeight: "auto",
    },
    caption: {
      fontSize: "1.25rem",
      fontWeight: 700,
      lineHeight: "auto",
    },
    button_l: {
      fontSize: "1.25rem",
      fontWeight: 500,
      lineHeight: "auto",
    },
    button_m: {
      fontSize: "1rem",
      fontWeight: 500,
      lineHeight: "auto",
    },
    button_s: {
      fontSize: "0.875rem",
      fontWeight: 500,
      lineHeight: "auto",
    },
    button_xs: {
      fontSize: "12px",
      fontWeight: 500,
      lineHeight: "auto",
    },
    menu_tab: {
      fontSize: "1rem",
      fontWeight: 500,
      lineHeight: "auto",
    },
    body1: {
      fontSize: "1rem",
      fontWeight: 400,
      lineHeight: "22.4px",
    },
    body2: {
      fontSize: "0.875rem",
      fontWeight: 500,
      lineHeight: "18.2px",
    },
  },
  breakpoints: {
    values: {
      xs: 320,
      sm: 600,
      md: 960,
      lg: 1280,
      xl: 1920,
    } as BreakpointValues,
  },
  components: {
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: 0,
          paddingTop: 14,
          borderRadius: "16px",
          "&:last-child": {
            paddingBottom: 0,
          },
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        asterisk: {
          color: "red",
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          boxShadow: "none",
          background: "",
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: ({ theme }: { theme: any }) => ({
          backgroundColor: theme.palette.background.paper,
        }),
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: ({ theme }: { theme: any }) => ({
          backgroundColor: theme.palette.background.paper,
        }),
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: ({ theme }: { theme: any }) => ({
          "& .MuiOutlinedInput-root": {
            backgroundColor: theme.palette.input_field.fill,
          },
        }),
      },
    },
    MuiFormControl: {
      styleOverrides: {
        root: ({ theme }: { theme: any }) => ({
          "& .MuiOutlinedInput-root": {
            backgroundColor: theme.palette.input_field.fill,
          },
        }),
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        root: ({ theme }: { theme: any }) => ({
          "& .MuiOutlinedInput-root": {
            backgroundColor: theme.palette.input_field.fill,
          },
        }),
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: ({ theme }: { theme: any }) => ({
          backgroundColor: theme.palette.background.default,
        }),
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: "capitalize" as const,
        },
      },
    },
  },
  shape: {
    borderRadius: 4,
  },
};

// Light Theme
const lightTheme = createTheme({
  ...baseTheme,
  palette: {
    mode: "light" as PaletteMode,
    primary: {
      main: "#1677FF",
    },
    secondary: {
      main: "#4592FF",
    },
    tertiary: {
      main: "#D0E4FF",
    },
    background: {
      default: "#F7F7F7",
      paper: "#ffffff",
      bgShade_1: "#F9FAFD",
      bgShade_2: "#FAFAFA",
      bgShade_3: "#F4F4F4",
    },
    text: {
      primary: "#1F1D2B",
      secondary: "#6C7184",
      tertiary: "#747474",
    },
    greyscale: {
      icons: "#7C7B7B",
      stroke_1: "#D7D7D7",
      divider_1: "#EDEDED",
      disabledText: "#CFD6DC",
      disabledBg: "#EFEFEF",
      white: "#ffffff",
    },
    system: {
      success: "#28A745",
      successHover: "#7CCA8D",
      successSecondary: "#5CB85C",
      successTertiary: "#E6FFE2",
      successClick: "#208637",
      error: "#E74C3C",
      errorHover: "#F1948A",
      errorSecondary: "#F78578",
      errorTertiary: "#FDEDEC",
      errorClick: "#B93D30",
      returned: "#FFA500",
      returnedHover: "#FFC966",
      returnedSecondary: "#F7AC22",
      returnedTertiary: "#FFF6E6",
      returnedClick: "#CC8400",
      pending: "#F39C12",
      pendingHover: "#F8C471",
      pendingSecondary: "#F1C40F",
      pendingTertiary: "#FDEBD0",
      pendingClick: "#C27D0E",
      submit: "#3498DB",
      submitHover: "#85C1E9",
      submitSecondary: "#5DADE2",
      submitTertiary: "#EAF7FD",
      submitClick: "#2A7AAF",
    },
    button: {
      // Primary Button
      primaryBg: "#1677FF",
      primaryText: "#FFFFFF",
      primaryBgHover: "#D0E4FF",
      primaryTextHover: "#1677FF",
      primaryBgFocused: "#125FCC",
      primaryStrokeFocused: "#0D4799",
      primaryTextFocused: "#FFFFFF",
      primaryBgActive: "#73ADFF",
      primaryTextActive: "#FFFFFF",
      primaryBgClick: "#125FCC",
      primaryTextClick: "#FFFFFF",
      primaryBgDisabled: "#B9D6FF",
      primaryTextDisabled: "#FFFFFF",
      // Secondary Button
      secondaryBg: "#FFFFFF",
      secondaryText: "#1677FF",
      secondaryBgHover: "#D0E4FF",
      secondaryTextHover: "#1677FF",
      secondaryBgFocused: "#D0E4FF",
      secondaryStrokeFocused: "#125FCC",
      secondaryTextFocused: "#1677FF",
      secondaryBgActive: "#73ADFF",
      secondaryTextActive: "#1677FF",
      secondaryBgClick: "#E8F1FF",
      secondaryTextClick: "#1677FF",
      secondaryBgDisabled: "#FFFFFF",
      secondaryTextDisabled: "#CFD6DC",
      // Cancel Button
      cancelBg: "#E74C3C",
      cancelText: "#FFFFFF",
      cancelBgHover: "#F1948A",
      cancelTextHover: "#FFFFFF",
      cancelBgFocused: "#B93D30",
      cancelStrokeFocused: "#8B2E24",
      cancelTextFocused: "#FFFFFF",
      cancelBgActive: "#EC7063",
      cancelTextActive: "#FFFFFF",
      cancelBgClick: "#B93D30",
      cancelTextClick: "#FFFFFF",
      cancelBgDisabled: "#F5B7B1",
      cancelTextDisabled: "#FFFFFF",
    },
    input_field: {
      fill: "#FBFDFF",
      stroke: "#c1c2c4",
      text: "#747474",
    },
  },
});

// Dark Theme
const darkTheme = createTheme({
  ...baseTheme,
  palette: {
    mode: "dark" as PaletteMode,
    primary: {
      main: "#0F61AC",
    },
    secondary: {
      main: "#3A7EBC",
    },
    tertiary: {
      main: "#D1DEF6",
    },
    background: {
      default: "#252836",
      paper: "#1F1D2B",
      bgShade_1: "#3C394A",
      bgShade_2: "#2D303E",
    },
    text: {
      primary: "#D9D9D9",
      secondary: "#9D9D9D",
      tertiary: "#5C5C5C",
    },
    greyscale: {
      icons: "#6C6C6C",
      stroke_1: "#ffffff1f",
      divider_1: "#323232",
      disabledText: "#8B8B8B",
      disabledBg: "#4A4A4A",
      white: "#ffffff",
    },
    system: {
      success: "#4DA63E",
      successHover: "#94CA8B",
      successSecondary: "#B0F9A5",
      successTertiary: "#C2F5B9",
      successClick: "#3E8532",
      error: "#DB4B4C",
      errorHover: "#E99394",
      errorSecondary: "#F55656",
      errorTertiary: "#F8DBDB",
      errorClick: "#AF3C3D",
      returned: "#DE8200",
      returnedHover: "#E59B33",
      returnedSecondary: "#F59A19",
      returnedTertiary: "#F8E6CC",
      returnedClick: "#B26800",
      pending: "#FFB742",
      pendingHover: "#FFD48E",
      pendingSecondary: "#EFE29B",
      pendingTertiary: "#F9F1C7",
      pendingClick: "#D19635",
      submit: "#5AA1C2",
      submitHover: "#7BB4CE",
      submitSecondary: "#2A8FC6",
      submitTertiary: "#DEECF3",
      submitClick: "#48819B",
    },
    button: {
      // Primary Button
      primaryBg: "#0F61AC",
      primaryText: "#FFFFFF",
      primaryBgHover: "#D0E4FF",
      primaryTextHover: "#0F61AC",
      primaryBgFocused: "#0C4E8A",
      primaryStrokeFocused: "#093A67",
      primaryTextFocused: "#FFFFFF",
      primaryBgActive: "#3A7EBC",
      primaryTextActive: "#FFFFFF",
      primaryBgClick: "#0C4E8A",
      primaryTextClick: "#FFFFFF",
      primaryBgDisabled: "#90B8DD",
      primaryTextDisabled: "#FFFFFF",
      // Secondary Button
      secondaryBg: "#FFFFFF",
      secondaryText: "#0F61AC",
      secondaryBgHover: "#A6C7E5",
      secondaryTextHover: "#0F61AC",
      secondaryBgFocused: "#90B8DD",
      secondaryStrokeFocused: "#0C4E8A",
      secondaryTextFocused: "#0F61AC",
      secondaryBgActive: "#659BCC",
      secondaryTextActive: "#0F61AC",
      secondaryBgClick: "#A6C7E5",
      secondaryTextClick: "#0F61AC",
      secondaryBgDisabled: "#FFFFFF",
      secondaryTextDisabled: "#CFD6DC",
      // Cancel Button
      cancelBg: "#B84C4B",
      cancelText: "#FFFFFF",
      cancelBgHover: "#C6706F",
      cancelTextHover: "#FFFFFF",
      cancelBgFocused: "#933D3C",
      cancelStrokeFocused: "#6E2E2D",
      cancelTextFocused: "#FFFFFF",
      cancelBgActive: "#C6706F",
      cancelTextActive: "#FFFFFF",
      cancelBgClick: "#933D3C",
      cancelTextClick: "#FFFFFF",
      cancelBgDisabled: "#E3B7B7",
      cancelTextDisabled: "#FFFFFF",
    },
    input_field: {
      fill: "#2D303E",
      stroke: "#5e606b",
      text: "#ABBBC2",
    },
  },
});

export { lightTheme, darkTheme };